import useZkLayer from '../hooks/useZkLayer';
import { allowedTypes, fitTypes, ml } from '../lib/profiles';
import { attachDeclutter } from '../lib/declutter';
import { uiZoom } from '../lib/uiScale';
import { SurveyLegend } from './CoordList';
import { useFloorPlanLayer } from './FloorPlans';
import { useSiteLayer } from './SitePlans';
import { useWmsLayers } from './MapDocsExtra';
import { useDocPins } from './DocPins';
import useRainLayer, { usePreviewLayer } from '../hooks/useRainLayer';
import useDryLayer from '../hooks/useDryLayer';
import useAquaLayer from '../hooks/useAquaLayer';
import useSewLayer from '../hooks/useSewLayer';
import { passClick } from '../lib/mapPass';
import useSprayLayer from '../hooks/useSprayLayer';
import { pipeLatLngs } from '../lib/model';
import { forwardRef, lazy, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { GloveBtn } from './GloveMode';
import Compass from './Compass';
import L from 'leaflet';
import '../lib/mapRotate';
import 'leaflet/dist/leaflet.css';
import { Eye, EyeOff, Layers, Lock, LockOpen, Check, ListTree, Move, Maximize2, X, Footprints, Flame, ScanSearch, LocateFixed, Settings2, Target, ChevronUp, ChevronDown, DraftingCompass, Rotate3d, Mountain, Image as ImageIcon, ImageOff } from 'lucide-react';
import MaintFilter from './MaintFilter';
import OverlayPanel from './OverlayPanel';
import OverlayControls from './OverlayControls';
import { openStreetView } from '../lib/maint';
import { pitRing } from '../lib/excavation';
import { mainPhotoOf, isLocked, fitBox, PIPE_TYPES, FITTING_TYPES } from '../lib/model';
import useNetworkLayers, { fittingHtml, nearestOnPipes } from './networkLayers';
import { symStyle, setSymStyle } from '../lib/fitSymbols';
import useLiveLocation from './useLiveLocation';
import usePitLayers from './pitLayers';
import useMediaLayers from './mediaLayers';
import useHeatLayer from './heatLayer';
import { GnssChip, GnssPlaceBar, useGnssMarker } from './GnssParts';
import { RtkBadge } from './PrecisionDialog';
import TargetSearch from './TargetSearch';
import { useNetCheckLayer } from './NetCheck';
import { useAreaEditLayer } from './AreaEditor';
import { editArea, useAe, copyName, closeArea } from '../lib/areaEdit';
import { olcEncode } from '../lib/olc';
import { routeLink } from '../lib/route';
import useLoggerLayer, { usePlanLayer, useNoiseLayer, useSuggestLayer } from './loggerLayer';
import { useMapUi, MapUiMenu } from './MapUi';
import LeisureLegend from './LeisureLegend';
import GardenLegend from './GardenLegend';
import BuildingLegend from './BuildingLegend';
import { getMode } from '../lib/profiles';
const Map3D = lazy(() => import('./Map3D'));
import { snoozeWalk } from '../lib/walk';
import useSurveyLayer from './surveyLayer';
import useTrackLayer from './trackLayer';
import { useCadLayer, cadLayers, colorOf, cadPts } from './CadPanel';
import { usePlanCheckLayer } from './PlanCheck';
import { pressureMap } from '../lib/pressure';
import { openGarden } from './GardenCalc';
import { useDrawLayer } from './drawLayer';
import useFaultLayer from './faultLayer';
import DrawLegend from './DrawLegend';

const selDrawing = (id) => window.dispatchEvent(new CustomEvent('giss-draw-sel', { detail: id }));
import { AltChip } from './AltParts';
import MaskToggle from './MaskToggle';
import { HelpHere } from './TourGuide';
import { useVis, pipeCat, fitCat, openVis, setVis, flyTo } from '../lib/vis';
import { useFeatures, feat } from '../lib/features';
import useDeclutter from '../hooks/useDeclutter';
import { LAYERS } from '../lib/mapUrls';
import { useBaseMaps, makeBase } from './useBaseMaps';
import { extOk } from '../lib/policy';
// point types of detection tools: in the legend only when the tool is on
const FIT_FEAT = { kks: 'kks', xbear: 'xbear', cfault: 'fault', locate: 'locate', tracer: 'tracer', gmic: 'tracer' };

export { LAYERS } from '../lib/mapUrls';
const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const NONE = [];
// working zone of a project: convex hull of project + report points, pushed ~25 m outwards
export function projectZone(pts) {
  const P = pts.filter((p) => p[0] != null && p[1] != null);
  if (P.length < 2) return null;
  const s = [...P].sort((a, b) => a[1] - b[1] || a[0] - b[0]), cr = (o, a, b) => (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);
  const half = (arr) => arr.reduce((h, p) => { while (h.length > 1 && cr(h[h.length - 2], h[h.length - 1], p) <= 0) h.pop(); h.push(p); return h; }, []);
  const lo = half(s), up = half([...s].reverse()), hull = [...lo.slice(0, -1), ...up.slice(0, -1)];
  const c = [P.reduce((a, p) => a + p[0], 0) / P.length, P.reduce((a, p) => a + p[1], 0) / P.length], kx = Math.cos((c[0] * Math.PI) / 180);
  const la = P.map((p) => p[0]), ln = P.map((p) => p[1]), [a0, a1, b0, b1] = [Math.min(...la), Math.max(...la), Math.min(...ln), Math.max(...ln)];
  if (a1 - a0 < 1e-7 && b1 - b0 < 1e-7) return null;
  const out = hull.length > 2 ? hull : [[a0, b0], [a0, b1], [a1, b1], [a1, b0]];
  return out.map((p) => { const dy = (p[0] - c[0]) * 111320, dx = (p[1] - c[1]) * 111320 * kx, d = Math.hypot(dx, dy) || 1, k = (d + 25) / d; return [c[0] + (dy * k) / 111320, c[1] + (dx * k) / (111320 * kx)]; });
}

const MapView = forwardRef(function MapView({ t, blank = false, projectPin, reports, waitMap, pipes: allPipes, fittings: allFittings = [], dryStrict, irrProject, floorPlan, sites, docPins, onDocPin, floorBar, draft, onMapClick, netUnlocked, fitFilter, onFittingClick, onMoveFitting, onMoveVertex, onLineClick, onDeleteVertex, onNetConfirm, activeId, onSelect, onConfirm, onUnlock, report, overlayVisible, onToggleOverlay, fullscreen, onToggleFullscreen, follow, pits: allPits, onPitClick, selId, onPipeClick, onMovePipe, onPitChange, setDraft, onMediaClick, onMoveMedia, onPhoto, onGallery, onXray, onOverlayChange, lastId, onGnssPlace, gnssLineOn, loggers: allLoggers = [], loggerPlan, noise = [], tracks = [], cad = null, onCadLayer, pressureCfg, gardens, drawings, faults, loggerSel, onLoggerClick, onFitAll, onGps, children }, ref) {
  const elRef = useRef(null);
  const getLeaflet = useCallback(() => mapRef.current, []); // eslint-disable-line react-hooks/exhaustive-deps
  const tcRef = useRef(null), wrapRef = useRef(null);
  useEffect(() => {
    const ro = new ResizeObserver(() => wrapRef.current?.style.setProperty('--tc-h', `${tcRef.current?.offsetHeight || 0}px`));
    if (tcRef.current) ro.observe(tcRef.current);
    return () => ro.disconnect();
  }, []);
  const mapRef = useRef(null);
  const vis = useVis();
  const ft = useFeatures();
  const pipes = useMemo(() => (allPipes || []).filter((p) => vis[pipeCat(p)]), [allPipes, vis]);
  const fittings = useMemo(() => allFittings.filter((f) => vis[fitCat(f)]), [allFittings, vis]);
  const loggers = vis.loggers ? allLoggers : [];
  const pits = useMemo(() => (vis.pits ? allPits || [] : []), [allPits, vis.pits]);
  const tileRef = useRef(null);
  const groupRef = useRef(null);
  const geoRef = useRef(null);
  const pendingRef = useRef(null);
  const clickRef = useRef(null);
  clickRef.current = onMapClick;
  const [center, setCenter] = useState(null);
  const [layer, setLayer] = useState('hybrid');
  const baseMaps = useBaseMaps(t);
  const [legend, setLegend] = useState(false);
  const [sym, setSym] = useState(symStyle());
  useEffect(() => { const h = () => setSym(symStyle()); window.addEventListener('giss-sym-style', h); return () => window.removeEventListener('giss-sym-style', h); }, []);
  const [cadView, setCadView] = useState(false);
  const [maintFilter, setMaintFilter] = useState([]);
  const [ovOpen, setOvOpen] = useState(false);
  const [tileOp, setTileOp] = useState(100);
  const [heat, setHeat] = useState(false);
  const ui = useMapUi();
  const [uiMenu, setUiMenu] = useState(false);
  const [tilt, setTilt] = useState(0);
  const [m3d, setM3d] = useState(null);
  const open3d = () => {
    if (!navigator.onLine) { toast.message(t.m3Offline); setTilt(2); return; }
    const c = mapRef.current.getCenter();
    setM3d({ center: { lat: c.lat, lng: c.lng }, zoom: mapRef.current.getZoom() });
  };
  const [tsOpen, setTsOpen] = useState(false);
  const tgtRef = useRef(null);
  useEffect(() => {
    const on = () => setUiMenu(true);
    window.addEventListener('giss-ui-menu', on);
    return () => window.removeEventListener('giss-ui-menu', on);
  }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const cl = document.body.classList;
    const a = () => cl.add('map-moving'), b = () => cl.remove('map-moving');
    map.on('movestart zoomstart dragstart', a).on('moveend zoomend dragend', b);
    return () => { map.off('movestart zoomstart dragstart', a).off('moveend zoomend dragend', b); b(); };
  }, [center == null]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.body.classList.toggle('ui-free', !!ui.hidden); return () => document.body.classList.remove('ui-free'); }, [ui.hidden]);
  useEffect(() => {
    const on = (e) => setMaintFilter(e.detail || []);
    window.addEventListener('giss-maint-filter', on);
    return () => window.removeEventListener('giss-maint-filter', on);
  }, []);

  const svyRef = useRef(null);
  const stkRef = useRef(null);
  useImperativeHandle(ref, () => ({
    getBounds: () => { const b = mapRef.current?.getBounds(); return b ? [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()] : null; },
    getCenter: () => {
      if (!mapRef.current) return pendingRef.current ? { lat: pendingRef.current[0], lng: pendingRef.current[1], zoom: pendingRef.current[2] } : { lat: 46.06, lng: 11.12, zoom: 18 };
      const c = mapRef.current.getCenter();
      return { lat: c.lat, lng: c.lng, zoom: mapRef.current.getZoom() };
    },
    setView: (lat, lon, zoom) => {
      if (!mapRef.current) { pendingRef.current = [lat, lon, zoom ?? 18]; return; }
      mapRef.current.setView([lat, lon], zoom ?? mapRef.current.getZoom());
    },
    svyMarks: (pts) => {
      const map = mapRef.current;
      if (!map) return;
      svyRef.current?.remove();
      svyRef.current = pts.length ? L.layerGroup(pts.map((p, i) => L.marker(p, { interactive: false, icon: L.divIcon({ className: 'svy-mk-wrap', html: `<span class="svy-mk">P${i + 1}</span>`, iconSize: [0, 0] }) }))).addTo(map) : null;
    },
    targetMark: (h) => {
      const map = mapRef.current;
      tgtRef.current?.remove(); tgtRef.current = null;
      if (!map || !h) return;
      const el = document.createElement('div');
      el.className = 'tgt-pop';
      el.innerHTML = `<b>🎯 ${esc(h.name)}</b><small>${h.lat.toFixed(7)}, ${h.lon.toFixed(7)}</small><small>Plus Code: <b data-testid="target-pluscode">${olcEncode(h.lat, h.lon)}</b></small><div class="row"><a class="btn btn-green btn-sm" target="_blank" rel="noreferrer" href="${routeLink({ lat: h.lat, lon: h.lon })}" data-testid="target-nav-link">${esc(t.alNav)}</a><button type="button" class="btn btn-outline btn-sm" data-testid="target-remove-btn">× ${esc(t.tsRemove)}</button></div>`;
      const mk = L.marker([h.lat, h.lon], { zIndexOffset: 4000, icon: L.divIcon({ className: 'svy-mk-wrap', html: '<span class="tgt-mk" data-testid="target-marker"><i></i>🎯</span><button type="button" class="tgt-x" data-testid="target-marker-x">×</button>', iconSize: [0, 0] }) });
      const drop = () => { mk.remove(); if (tgtRef.current === mk) tgtRef.current = null; };
      el.querySelector('[data-testid="target-remove-btn"]').onclick = drop;
      mk.bindPopup(el, { offset: [0, -16] }).addTo(map);
      mk.on('click', (e) => { if (e.originalEvent?.target?.classList?.contains('tgt-x')) drop(); });
      tgtRef.current = mk;
      map.setView([h.lat, h.lon], Math.max(map.getZoom(), 18));
      setTimeout(() => tgtRef.current === mk && mk.openPopup(), 400);
    },
    stakeMark: (pt) => {
      stkRef.current?.remove();
      stkRef.current = pt && mapRef.current ? L.marker(pt, { interactive: false, zIndexOffset: 3000, icon: L.divIcon({ className: 'svy-mk-wrap', html: '<span class="stake-mk" data-testid="stake-map-target">✛</span>', iconSize: [0, 0] }) }).addTo(mapRef.current) : null;
      if (pt) mapRef.current?.setView(pt, Math.max(mapRef.current.getZoom(), 19));
    },
    nearestPipe: (pt, onlyId) => (mapRef.current ? nearestOnPipes(mapRef.current, L.latLng(pt[0], pt[1]), netRef.current.pipes, netRef.current.fittings, 16, onlyId) : null),
    // tiles along a route corridor (zoom 12-16, ±1 tile) for offline use
    routeTileJobs: (pts) => {
      const map = mapRef.current;
      if (!map || !pts?.length) return [];
      const [tpl, maxZoom] = LAYERS[layer] || LAYERS.hybrid, seen = new Set(), urls = [];
      for (let z = 12; z <= Math.min(16, maxZoom); z++) {
        pts.forEach(([la, lo]) => {
          const p = map.project([la, lo], z).divideBy(256).floor();
          for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
            const k = `${z}/${p.x + dx}/${p.y + dy}`;
            if (seen.has(k)) continue;
            seen.add(k);
            urls.push(tpl.replace('{s}', 'a').replace('{x}', p.x + dx).replace('{y}', p.y + dy).replace('{z}', z));
          }
        });
      }
      return urls;
    },
    tileJobs: (extra = 2) => {
      const map = mapRef.current;
      if (!map) return [];
      const [tpl, maxZoom] = LAYERS[layer] || LAYERS.hybrid;
      const b = map.getBounds();
      const z0 = map.getZoom();
      const urls = [];
      for (let z = Math.max(z0 - 1, 1); z <= Math.min(z0 + extra, maxZoom, 20); z++) {
        const nw = map.project(b.getNorthWest(), z).divideBy(256).floor();
        const se = map.project(b.getSouthEast(), z).divideBy(256).floor();
        for (let x = nw.x; x <= se.x; x++) for (let y = nw.y; y <= se.y; y++) {
          urls.push(tpl.replace('{s}', 'a').replace('{x}', x).replace('{y}', y).replace('{z}', z));
        }
      }
      return urls;
    },
    fitAll: (pts, left = 60) => {
      if (!pts.length || !mapRef.current) return;
      if (pts.length === 1) mapRef.current.setView(pts[0], 18);
      else mapRef.current.fitBounds(L.latLngBounds(pts), { paddingTopLeft: [left, 60], paddingBottomRight: [60, 60], maxZoom: 19 });
    },
  }));

  useEffect(() => {
    const [lat, lon, z] = pendingRef.current || (report ? [report.lat, report.lon, report.zoom || 18] : [46.06, 11.12, 18]);
    const map = L.map(elRef.current, { zoomControl: true, maxZoom: 24, rotate: true, bearing: 0 }).setView([lat, lon], z);
    mapRef.current = map;
    // symbols keep their real-world proportion to the pipes when zooming (clamped so they stay visible)
    const fz = () => map.getContainer().style.setProperty('--fz', String(Math.min(3, Math.max(0.3, 2 ** (map.getZoom() - 19)))));
    map.on('zoom zoomend', fz); fz();
    // pinch zoom: re-project vector layers each frame so pipes don't drift away from the fitting markers
    let zr = 0;
    map.on('zoom', () => {
      if (map._animatingZoom) return;
      cancelAnimationFrame(zr);
      zr = requestAnimationFrame(() => [map._renderer, ...Object.values(map._paneRenderers || {})].forEach((r) => r?._map && r._reset()));
    });
    map.on('rotate', () => map.getContainer().style.setProperty('--brg', `${map.getBearing()}deg`));
    map.createPane('geoOverlay');
    map.getPane('geoOverlay').style.zIndex = 450;
    map.getPane('geoOverlay').style.pointerEvents = 'none';
    map.createPane('pits');
    map.getPane('pits').style.zIndex = 455;
    map.createPane('pipes');
    map.getPane('pipes').style.zIndex = 460;
    geoRef.current = L.layerGroup().addTo(map);
    groupRef.current = L.layerGroup().addTo(map);
    map.on('click', (e) => { if (window.__gissAreaEdit && !window.__gissDrafting) return; if (window.__gissStudio) return window.dispatchEvent(new CustomEvent('giss-map-click', { detail: [e.latlng.lat, e.latlng.lng] })); clickRef.current?.(e.latlng); });
    const upd = () => { const c = map.getCenter(); setCenter({ lat: c.lat, lng: c.lng, z: map.getZoom() }); };
    map.on('move zoom', upd);
    upd();
    const offDcl = attachDeclutter(map, uiZoom);
    return () => { offDcl(); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const netRef = useRef({});
  netRef.current = { pipes, fittings };
  const press = useMemo(() => pressureMap(pressureCfg, allPipes || [], allFittings), [pressureCfg, allPipes, allFittings]);
  useNetCheckLayer(mapRef, pipes, fittings);
  useSprayLayer(mapRef, fittings);
  useDryLayer(mapRef, gardens, allFittings, t, dryStrict);
  useAquaLayer(mapRef, t);
  useSewLayer(mapRef, t, allFittings);
  useRainLayer(mapRef, gardens, irrProject, t);
  usePreviewLayer(mapRef);
  useFloorPlanLayer(mapRef, floorPlan);
  useSiteLayer(mapRef, floorPlan ? null : sites);
  useWmsLayers(mapRef);
  useDocPins(mapRef, docPins, onDocPin);
  useZkLayer(mapRef, allPipes, allFittings, t);
  useNetworkLayers(mapRef, 0, { press, t, pipes, fittings, draft, netUnlocked, fitFilter, maintFilter, onFittingClick, onMoveFitting, onMoveVertex, onLineClick, onDeleteVertex, selId, onPipeClick, onMovePipe, symStyle: sym });
  useMediaLayers(mapRef, { reports, draft, selId, onMediaClick, onMoveMedia });

  useEffect(() => {
    const map = mapRef.current;
    if (tileRef.current) map.removeLayer(tileRef.current);
    tileRef.current = null;
    if (cadView) return;
    const base = makeBase(layer, baseMaps.maps);
    tileRef.current = base && base.addTo(map);
  }, [layer, cadView, baseMaps.maps, baseMaps.kritis]); // eslint-disable-line react-hooks/exhaustive-deps
  // chosen map no longer available (KRITIS on, file deleted) -> first allowed one
  useEffect(() => { if (!baseMaps.list.some(([k]) => k === layer)) setLayer(baseMaps.list[0][0]); }, [baseMaps.list, layer]);
  useEffect(() => { if (!cad) setCadView(blank); }, [cad, blank]);
  const toggleCadView = () => {
    const on = !cadView;
    setCadView(on);
    if (on && cad) { setVis({ cad: true }); flyTo(cadPts(cad), true); }
  };

  useEffect(() => {
    const g = groupRef.current;
    g.clearLayers();
    reports.forEach((r, i) => {
      const active = r.id === activeId;
      const html = `<div class="rep-pin ${active ? 'active-pin' : ''} ${r.done ? 'done' : ''}" data-testid="map-pin-${r.id}"><b class="rep-pin-n">${projectPin ? i + 1 : '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M9 4h6l1 2h3v14H5V6h3z"/><path d="M9 12h6M9 16h4"/></svg>'}</b>
        <span>${esc(r.title)}</span>${waitMap?.[r.id] ? `<i class="wait-dot" style="background:${waitMap[r.id]}" data-testid="wait-dot-${r.id}"></i>` : ''}</div>`;
      const icon = L.divIcon({ html, className: 'pin-wrap', iconSize: [34, 44], iconAnchor: [17, 44] });
      const m = L.marker([r.lat, r.lon], { icon, zIndexOffset: active ? 1000 : 0 });
      m.on('click', () => onSelect(r.id));
      g.addLayer(m);
    });
    if (projectPin) {
      // zone follows everything drawn in the project, incl. the line being drawn right now
      const work = [...reports.map((r) => [r.lat, r.lon]), ...(gardens || []).flatMap((x) => x.pts || []), ...allPipes.flatMap((p) => pipeLatLngs(p, allFittings)), ...(draft?.kind === 'pipe' ? pipeLatLngs(draft, allFittings) : []), ...allFittings.map((f) => [f.lat, f.lon]), ...(pits || []).flatMap(pitRing), ...reports.flatMap((r) => (r.photos || []).filter((m) => m.lat != null).map((m) => [m.lat, m.lon]))].filter((x) => x && x[0] != null);
      const zone = projectZone(work.length ? work : [[projectPin.lat, projectPin.lon]]);
      if (zone) g.addLayer(L.polygon(zone, { color: projectPin.color || '#0A2540', weight: 3, dashArray: '10 6', fillColor: projectPin.color || '#FACC15', fillOpacity: 0.08, interactive: false, className: 'project-zone' }));
      const html = `<div class="project-map-pin" data-testid="map-project-pin"><span class="pp-icon"><svg viewBox="0 0 24 24" width="22" height="22" fill="#FACC15"><path d="M5 21V4h11l-2 4 2 4H7v9z"/></svg></span><span class="pp-label">${esc(projectPin.name)}</span><b class="pp-count" data-testid="map-project-count">${reports.length}</b></div>`;
      const mid = zone ? [zone.reduce((a, p) => a + p[0], 0) / zone.length, zone.reduce((a, p) => a + p[1], 0) / zone.length] : work[0] || [projectPin.lat, projectPin.lon];
      // the flag sits on the top edge of the project zone (never on fields / labels), a thin line points to the real spot
      const top = zone ? zone.reduce((a, p) => (p[0] > a[0] ? p : a), zone[0]) : null;
      const real = projectPin.lat != null ? [projectPin.lat, projectPin.lon] : mid;
      const pc = top || mid;
      if (top) g.addLayer(L.polyline([top, real], { color: projectPin.color || '#0A2540', weight: 1.5, opacity: 0.8, dashArray: '3 5', interactive: false, className: 'pp-leader' }));
      const pm = L.marker(pc, { icon: L.divIcon({ html, className: 'pin-wrap pp-wrap', iconSize: [40, 40], iconAnchor: top ? [20, 40] : [20, 20] }), zIndexOffset: 2000 });
      const list = `<div class="pp-list" data-testid="map-project-list"><b>${esc(projectPin.name)}</b><small>${esc(t.reports)}: ${reports.length}</small>${reports.map((r, i) => `<button type="button" data-rid="${r.id}" data-testid="map-project-list-${i}"><i>${i + 1}</i>${esc(r.title)}<em>${(r.photos || []).length} 📷</em></button>`).join('')}</div>`;
      pm.bindPopup(list, { className: 'pp-popup', maxWidth: 280 });
      pm.on('popupopen', (e) => e.popup.getElement().querySelectorAll('[data-rid]').forEach((b) => { b.onclick = () => { onSelect(b.dataset.rid); pm.closePopup(); }; }));
      g.addLayer(pm);
    }
  }, [reports, activeId, onSelect, projectPin, waitMap, t, allPipes, allFittings, pits, draft, gardens]);

  useDeclutter(mapRef, []);
  useEffect(() => { elRef.current.classList.toggle('draw-cursor', !!draft); window.__gissDrafting = !!draft; if (draft && window.__gissAreaEdit) closeArea(true); return () => { window.__gissDrafting = false; }; }, [draft]);
  // route planned by the LeakBuster (plan_route tool): dashed purple line, fitted into view
  useEffect(() => {
    let line = null;
    window.__gissMapCenter = () => mapRef.current?.getCenter();
    window.__gissMapView = () => { const m = mapRef.current; return m ? { m, c: m.getCenter(), z: m.getZoom(), s: m.getSize() } : null; };
    const on = (e) => {
      line?.remove(); line = null;
      const map = mapRef.current, d = e.detail;
      if (!map || !d?.pts?.length) return;
      line = L.featureGroup([L.polyline(d.pts, { color: '#7c3aed', weight: 5, opacity: 0.85, dashArray: '10 8', className: 'agent-route' }).bindTooltip(d.name || '', { sticky: true }),
        ...(d.stops || []).map((s) => L.marker([s.lat, s.lon], { icon: L.divIcon({ className: 'pin-wrap', html: `<span class="route-stop ${s.kind}">${{ hut: '🏠', water: '🚰', shelter: '⛺' }[s.kind]}</span>`, iconSize: [30, 30], iconAnchor: [15, 15] }) }).bindTooltip(`${s.name || s.kind} · km ${s.at.toFixed(1)}`))]).addTo(map);
      if (!d.noFit) map.fitBounds(line.getBounds(), { padding: [40, 40] });
    };
    const nv = (e) => { const map = mapRef.current, d = e.detail; if (!map) return; map.setBearing?.(-d.bearing); map.panTo([d.lat, d.lon], { animate: true }); if (map.getZoom() < 16) map.setZoom(17); };
    window.addEventListener('giss-navi-view', nv);
    window.addEventListener('giss-agent-route', on);
    return () => { window.removeEventListener('giss-agent-route', on); window.removeEventListener('giss-navi-view', nv); line?.remove(); };
  }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || draft?.kind !== 'pipe' || !draft.points.length) return undefined;
    const pts = pipeLatLngs(draft, fittings), lastPt = pts[pts.length - 1];
    if (!lastPt) return undefined;
    const band = L.polyline([lastPt, map.getCenter()], { pane: 'pipes', color: '#f97316', weight: 3, dashArray: '4 8', opacity: 0.9, interactive: false, className: 'pipe-band' }).addTo(map);
    const mv = () => band.setLatLngs([lastPt, map.getCenter()]);
    map.on('move', mv);
    return () => { map.off('move', mv); band.remove(); };
  }, [draft, fittings]);
  useEffect(() => { const id = setTimeout(() => mapRef.current?.invalidateSize(), 60); return () => clearTimeout(id); }, [fullscreen]);
  // map box changes late (fullscreen, address bar, zoom, sheets): keep Leaflet's size exact so the crosshair = map centre
  useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => getLeaflet()?.invalidateSize({ pan: false }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveLocation(mapRef, !!(fullscreen && follow));
  useHeatLayer(mapRef, heat, fittings, pipes, pits, faults);
  useGnssMarker(mapRef);
  useSurveyLayer(mapRef, vis.dims, allFittings, allPipes);
  useTrackLayer(mapRef, vis.tracks, tracks, t);
  useCadLayer(mapRef, cad, vis.cad !== false);
  usePlanCheckLayer(mapRef, allPipes, allFittings, cad);
  const [drawDraft, setDrawDraft] = useState(null);
  useEffect(() => { const on = (e) => setDrawDraft(e.detail); window.addEventListener('giss-draw-draft', on); return () => window.removeEventListener('giss-draw-draft', on); }, []);
  const ae = useAe(), aeHide = ae?.hide;
  const shownDrawings = useMemo(() => (aeHide ? (drawings || []).filter((d) => `dr:${d.id}` !== aeHide) : drawings), [drawings, aeHide]);
  useDrawLayer(mapRef, shownDrawings, drawDraft, selDrawing);
  useAreaEditLayer(mapRef);
  useFaultLayer(mapRef, faults);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gardens?.length) return undefined;
    const gOpts = (x) => ({ pts: x.pts, hide: `ga:${x.id}`, label: x.name, onSave: (pts) => window.dispatchEvent(new CustomEvent('giss-garden-pts', { detail: { id: x.id, pts } })), extra: { label: t.aeCalc, onClick: () => openGarden(x.id) },
      duplicate: (pts) => { const c = { ...x, id: `ga_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: copyName(x.name, t), pts }; window.dispatchEvent(new CustomEvent('giss-garden-dup', { detail: c })); return gOpts(c); } });
    const editG = (x) => editArea(gOpts(x));
    const g = L.layerGroup(gardens.filter((x) => `ga:${x.id}` !== aeHide).map((x) => L.polygon(x.pts, { color: '#16a34a', weight: 2, fillOpacity: 0.18, dashArray: '6 4', className: `garden-area garden-${x.id}` }).bindTooltip(esc(x.name)).on('click', (e) => { L.DomEvent.stopPropagation(e); if (!passClick(e)) editG(x); }))).addTo(map);
    return () => g.remove();
  }, [gardens, aeHide, t]);
  useEffect(() => {
    const on = (e) => {
      const map = mapRef.current, { pts, legend: lg } = e.detail;
      if (!map || !pts.length) return;
      elRef.current.closest('[data-testid="map-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (lg) setLegend(true);
      setTimeout(() => {
        map.invalidateSize();
        if (pts.length === 1) map.flyTo(pts[0], 19, { duration: 1.2 });
        else map.flyToBounds(L.latLngBounds(pts), { padding: [50, 50], maxZoom: 21, duration: 1.2 });
      }, 350);
    };
    window.addEventListener('giss-fly', on);
    return () => window.removeEventListener('giss-fly', on);
  }, []);
  useLoggerLayer(mapRef, loggers, loggerSel, onLoggerClick);
  usePlanLayer(mapRef, vis.loggers ? loggerPlan : []);
  useSuggestLayer(mapRef);
  useNoiseLayer(mapRef, vis.noise ? noise : NONE, t);
  usePitLayers(mapRef, { pits, draft, onPitClick, t, selId, onPitChange, setDraft, maintFilter });

  const zoom = center?.z;
  useEffect(() => {
    const g = geoRef.current;
    g.clearLayers();
    if (zoom == null) return;
    reports.forEach((r) => (r.photos || []).forEach((m) => {
      if (!m.ov || m.kind === 'video' || !m.data || zoom >= 15) return;
      const box = fitBox(m.w, m.h), k = ((m.ovS || 100) / 100) * 2 ** (zoom - 19), w = box.w * k, h = box.h * k;
      if (w > 6000 || w < 4) return;
      const az = Math.max(30, Math.min(110, Math.min(w, h) * 0.3));
      const arrow = m.dir == null ? '' : `<span class="geo-ov-dir" data-testid="photo-overlay-dir-${String(m.id).replace(/[^\w-]/g, '')}" style="width:${az}px;height:${az}px;transform:translate(-50%,-50%) rotate(${m.dir}deg)"><svg viewBox="0 0 40 40" width="100%" height="100%"><circle cx="20" cy="20" r="18" fill="rgba(15,23,42,.55)" stroke="#fff" stroke-width="2"/><path d="M20 4 L29 27 L20 22 L11 27 Z" fill="#f97316" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></svg></span>`;
      const html = `<img src="${m.data}" alt="" class="geo-overlay-img" data-testid="photo-overlay-${String(m.id).replace(/[^\w-]/g, '')}" style="width:${w}px;height:${h}px;transform:translate(-50%,-50%) rotate(${m.ovA || 0}deg);opacity:${(m.ovO ?? 75) / 100}"/>` + arrow;
      g.addLayer(L.marker([m.lat ?? r.lat, m.lon ?? r.lon], { icon: L.divIcon({ html, className: 'geo-overlay-wrap', iconSize: [0, 0], iconAnchor: [0, 0] }), pane: 'geoOverlay', interactive: false, keyboard: false }));
    }));
    if (!overlayVisible) return;
    reports.forEach((r) => {
      const m = mainPhotoOf(r);
      if (!m || !isLocked(r)) return;
      const box = fitBox(m.w, m.h);
      const k = ((r.overlayScale || 100) / 100) * 2 ** (zoom - (r.zoom || 18));
      const w = box.w * k, h = box.h * k;
      if (w > 6000 || w < 4) return;
      const html = `<img src="${m.data}" alt="" class="geo-overlay-img" data-testid="geo-overlay-${r.id}" style="width:${w}px;height:${h}px;transform:translate(-50%,-50%) rotate(${r.overlayAngle || 0}deg);opacity:${(r.overlayOpacity ?? 75) / 100}"/>`;
      const icon = L.divIcon({ html, className: 'geo-overlay-wrap', iconSize: [0, 0], iconAnchor: [0, 0] });
      g.addLayer(L.marker([r.lat, r.lon], { icon, pane: 'geoOverlay', interactive: false, keyboard: false }));
    });
  }, [reports, overlayVisible, zoom]);

  const main = mainPhotoOf(report);
  const locked = isLocked(report);
  const showOv = overlayVisible && main && !locked;
  const fsBar = fullscreen && main && !draft && (!locked || ovOpen);
  const qfOnly = maintFilter.some((k) => k === 'leak' || k.startsWith('id:'));

  return (
    <div ref={wrapRef} className={`map-wrapper ${cadView ? 'cad-view' : ''} ${fullscreen ? 'map-fs' : ''} ${qfOnly ? 'qf-only' : ''} ${ui.hidden ? 'ui-hidden' : ''} ${tilt ? `tilt-${tilt}` : ''} ${ui.show('gps') ? '' : 'ui-no-gps'} ${ui.show('filter') ? '' : 'ui-no-filter'}`} data-testid="map-wrapper" style={{ '--tile-op': tileOp / 100 }}>
      <div ref={elRef} className="map-el" data-testid="leaflet-map" />
      {floorBar}
      <div className="crosshair" />
      <Compass t={t} getMap={getLeaflet} />
      <div className="map-left-stack" data-testid="map-left-stack">
        {ft.gnss && ui.show('rtk') && <RtkBadge t={t} testid="rtk-badge-map" />}
        {ui.show('gps') && <button className="chip" data-testid="map-gps-btn" title={t.btnGps} onClick={onGps}><LocateFixed size={14} /> {t.tsMyPos}</button>}
        {ui.show('target') && <button className="chip" data-testid="map-target-search-btn" title={t.tsTitle} onClick={() => setTsOpen(true)}><Target size={14} /> {t.tsBtn}</button>}
      </div>
      {tsOpen && <TargetSearch t={t} mapRef={ref} onClose={() => setTsOpen(false)} />}
      {showOv && (
        <div className="overlay-container">
          <img
            data-testid="map-overlay-image"
            src={main.data}
            alt="Overlay"
            className="overlay-img"
            style={{
              transform: `rotate(${report.overlayAngle}deg) scale(${report.overlayScale / 100})`,
              opacity: report.overlayOpacity / 100,
            }}
          />
        </div>
      )}
      {netUnlocked && !draft && (
        <div className="net-edit-bar" data-testid="net-edit-bar">
          <span title={t.netEditHint}><Move size={13} /> {t.netEditBadge}</span>
          <button className="btn btn-accent btn-sm pulse" data-testid="map-net-confirm-btn" onClick={onNetConfirm}><Check size={14} /> {t.btnNetConfirm}</button>
        </div>
      )}
      {drawings?.length > 0 && <DrawLegend t={t} drawings={drawings} />}
      {legend && (
        <div className="map-legend" data-testid="map-legend">
          <button type="button" className="icon-btn lg-close" data-testid="map-legend-close-btn" aria-label={t.close} onClick={() => setLegend(false)}><X size={14} /></button>
          <div className="chip-group lg-symstyle" data-testid="legend-sym-style"><small>{t.symStyle}</small>{['std', 'din'].map((k) => <button key={k} type="button" className={`chip seg ${sym === k ? 'on' : ''}`} data-testid={`legend-sym-${k}`} onClick={() => setSymStyle(k)}>{t['symStyle_' + k]}</button>)}</div>
          {getMode() === 'outdoor' ? <LeisureLegend t={t} project={irrProject} /> : getMode() === 'garden' ? <GardenLegend t={t} /> : getMode() === 'building' ? <BuildingLegend t={t} /> : getMode() === 'survey' ? <SurveyLegend t={t} project={irrProject} /> : <>
          <b>{ml(t, 'legendPipes')}</b>
          {Object.entries(PIPE_TYPES).filter(([k]) => !allowedTypes() || allowedTypes().includes(k)).map(([k, c]) => <div key={k} className="lg-row"><i className="lg-line" style={{ background: c }} />{ml(t, 'type_' + k)}</div>)}
          <b>{ml(t, 'legendFittings')}</b>
          {Object.keys(FITTING_TYPES).filter((k) => (!fitTypes() || fitTypes().includes(k)) && (!FIT_FEAT[k] || feat(FIT_FEAT[k]))).map((k) => (
            <div key={k} className="lg-row"><span key={sym} dangerouslySetInnerHTML={{ __html: fittingHtml(t, { id: 'lg-' + k, type: k }) }} />{ml(t, 'fit_' + k)}</div>
          ))}
          </>}
          {cad && <b data-testid="legend-cad-title">{t.legendCad}</b>}
          {cad && cadLayers(cad).map(([k, n]) => {
            const off = (cad.off || []).includes(k);
            return <button key={k} type="button" className={`lg-row lg-cad ${off ? 'is-off' : ''}`} data-testid={`legend-cad-layer-${k}`} aria-pressed={!off} title={t.cadLayerToggle} onClick={() => onCadLayer?.(k)}><i className="lg-line" style={{ background: colorOf(k) }} />{k} <small>({n})</small>{off ? <EyeOff size={11} /> : <Eye size={11} />}</button>;
          })}
        </div>
      )}
      {!draft && !netUnlocked && main && (
        <div className={`mode-badge ${locked ? 'is-locked' : 'is-edit'}`} data-testid="lock-state-badge">
          {locked ? <Lock size={13} /> : <LockOpen size={13} />} {locked ? t.lockedBadge : t.editBadge}
        </div>
      )}
      {!locked && !draft && <div className="edit-frame" />}
      {draft && <div className="draw-frame" data-testid="draw-mode-frame" />}
      <button className={`chip ui-toggle ${ui.hidden ? 'on' : ''}`} data-testid="map-ui-toggle-btn" aria-pressed={ui.hidden} title={ui.hidden ? t.uiFreeOff : t.uiFree} onClick={() => { ui.set({ hidden: !ui.hidden }); setUiMenu(false); }}>
        {ui.hidden ? <ChevronDown size={14} /> : <ChevronUp size={14} />}{ui.hidden ? <Eye size={14} /> : <EyeOff size={14} />}<span>{ui.hidden ? t.uiFreeOff : t.uiFree}</span>
      </button>
      {tilt > 0 && <div className="tilt-hint" data-testid="map-tilt-hint">{t.m3TiltInfo}</div>}
      {m3d && <Suspense fallback={null}><Map3D t={t} center={m3d.center} zoom={m3d.zoom} pipes={allPipes} fittings={allFittings} onClose={() => setM3d(null)} /></Suspense>}
      <button type="button" className="walk-pill" data-testid="walk-hide-pill" onClick={snoozeWalk}><Footprints size={16} /> {t.walkPill}</button>
      {uiMenu && !ui.hidden && <MapUiMenu t={t} ui={ui} onClose={() => setUiMenu(false)} />}
      <div ref={tcRef} className="map-top-controls" data-testid="map-top-controls">
        <button className={`chip ${fullscreen ? 'fs-exit-chip' : 'fs-chip'}`} data-testid={fullscreen ? 'fs-exit-btn' : 'fs-enter-btn'} onClick={onToggleFullscreen}>
          {fullscreen ? <X size={14} /> : <Maximize2 size={14} />} {fullscreen ? t.fsExit : t.fsEnter}
        </button>
        {fullscreen && <GloveBtn t={t} testid="fs-glove-btn" />}
        {fullscreen && <HelpHere t={t} area="map" />}
        <button className={`chip ${uiMenu ? 'on-chip' : ''}`} data-testid="map-ui-settings-btn" title={t.uiMenuTitle} onClick={() => setUiMenu((v) => !v)}><Settings2 size={14} /> {t.uiTabsBtn}</button>
        {(cad || blank) && <button className={`chip cad-view-chip ${cadView ? 'on-chip' : ''}`} data-testid="map-cad-view-btn" aria-pressed={cadView} title={t.cadViewHint} onClick={toggleCadView}><DraftingCompass size={14} /> {cadView ? t.cadViewOff : t.cadViewOn}</button>}
        {ui.show('vis') && !['outdoor', 'garden', 'building'].includes(getMode()) && <button className="chip" data-testid="map-vis-btn" title={t.visTitle} onClick={openVis}><Eye size={14} /> {t.visShort}</button>}
        {ui.show('fit') && <button className="chip chip-strong" data-testid="map-fit-all-btn" title={t.btnFitAll} onClick={onFitAll}><ScanSearch size={14} /> {t.btnFitAllShort}</button>}
        {ui.show('gnss') && ft.gnss && <GnssChip t={t} />}
          {ui.show('gnss') && <AltChip t={t} />}
        {ui.show('legend') && <button className={`chip ${legend ? 'on-chip' : ''}`} data-testid="toggle-legend-btn" onClick={() => setLegend((v) => !v)}>
          <ListTree size={14} /> {t.legend}
        </button>}
        {ui.show('overlay') && !['outdoor', 'garden'].includes(getMode()) && <button className={`chip ${ovOpen ? 'on-chip' : ''}`} data-testid="toggle-overlay-btn" onClick={() => setOvOpen((v) => !v)}>
          {overlayVisible ? <ImageIcon size={14} /> : <ImageOff size={14} />} {t.btnToggleOv}
        </button>}
        {ui.show('heat') && getMode() !== 'garden' && (ft.leak || ft.maint || ft.fault) && <button className={`chip ${heat ? 'heat-chip' : ''}`} data-testid="toggle-heatmap-btn" aria-pressed={heat} title={t.heatHint} onClick={() => setHeat((v) => !v)}>
          <Flame size={14} /> {t.heatBtn}
        </button>}
        {ui.show('layer') && <label className="chip-group layer-pick" title={t.layerTitle}>
          <Layers size={14} className="chip-icon" />
          <select data-testid="map-layer-select" aria-label={t.layerTitle} value={layer} onChange={(e) => setLayer(e.target.value)}>
            {baseMaps.list.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>}
        {ui.show('3d') && <button className={`chip ${tilt ? 'on-chip' : ''}`} data-testid="map-tilt-btn" aria-pressed={!!tilt} title={t.m3TiltHint} onClick={() => setTilt((v) => (v + 1) % 3)}><Rotate3d size={14} /> {tilt ? `${t.m3TiltShort} ${tilt === 1 ? 35 : 55}°` : t.m3TiltShort}</button>}
        {ui.show('3d') && extOk('tiles') && <button className="chip" data-testid="map-3d-btn" title={t.m3Title} onClick={open3d}><Mountain size={14} /> 3D</button>}
        {ui.show('sv') && extOk('street') && <button className="chip" data-testid="map-streetview-btn" title={t.svHint} onClick={() => { const c = mapRef.current.getCenter(); openStreetView([c.lat, c.lng]); }}>
          <Footprints size={14} /> {t.svShort}
        </button>}
        {ui.show('filter') && ft.maint && <MaintFilter t={t} value={maintFilter} onChange={setMaintFilter} />}
      </div>
      <GnssPlaceBar t={t} onPlace={onGnssPlace} hidden={gnssLineOn} />
      {ovOpen && !['outdoor', 'garden'].includes(getMode()) && <OverlayPanel t={t} visible={overlayVisible} onToggle={onToggleOverlay} onPhoto={onPhoto} onGallery={onGallery} onXray={onXray} tileOp={tileOp} setTileOp={setTileOp} filter={maintFilter} setFilter={setMaintFilter} lastId={lastId} onClose={() => setOvOpen(false)} />}
      {fsBar && <div className="fs-ov-bar mask" data-testid="fs-overlay-bar"><MaskToggle t={t} id="fs-overlay" /><OverlayControls t={t} report={report} onChange={onOverlayChange} onConfirm={onConfirm} onUnlock={onUnlock} prefix="fs-" /></div>}
      <div className="map-bottom-bar">
        {ui.show('coords') && <div className="map-badge" data-testid="coord-badge">
          {center ? `${center.lat.toFixed(6)}, ${center.lng.toFixed(6)} · z${center.z}` : '…'}
        </div>}
        {fsBar || !main ? null : locked ? (
          <button className="btn btn-dark btn-sm lock-btn" data-testid="unlock-position-btn" onClick={onUnlock}>
            <LockOpen size={14} /> {t.btnUnlock}
          </button>
        ) : (
          <button className="btn btn-accent btn-sm pulse" data-testid="confirm-position-btn" onClick={onConfirm}>
            <Check size={14} /> {t.btnConfirmPos}
          </button>
        )}
      </div>
      {children}
    </div>
  );
});

export default MapView;
